import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { selectAppDomain, useAppSelector } from '@ibk/shared-config';
import { useFeatureFlag } from '@ibk/shared-feature-flags';
import { useTranslation } from '@ibk/shared-translation';
import { AppDomain, FeatureFlagUseCase } from '@ibk/shared-types';
import { Button, Icon, Tooltip } from '@ibk/shared-ui';
import { formatCurrency } from '@ibk/shared-utils';
import { useInvalidateConsentMutation } from '../../../../services/openFinanceApi';
import { ConsentBankState, ConsentPartner } from '../../../../services/types';
import styles from '../../ContabilidadeDigitalDasboard.module.css';
import { BankLogoAvatar } from '../BankLogoAvatar';
import { UnlinkAccountModal } from '../UnlinkAccountModal';
import { bankData, statesStyles, statesTranslationKeys } from './data';
const stateHasTooltip = [ConsentBankState.WAITING_SECOND_SIGNATURE, ConsentBankState.PENDING_MULTIPLE_SIGNATURES];
const getAccountToOpen = (connectedAccounts) => {
    let accountId = '';
    for (const account of connectedAccounts) {
        if (account.id && account.subtype === 'CHECKING_ACCOUNT') {
            accountId = account.id;
            break;
        }
    }
    return accountId;
};
export const BankAccountList = ({ consentBanks, 'data-testid': dataTestId, onCancelConnection, onInvalidateConsentStart, onInvalidateConsentEnd, onOpenTransactions, }) => {
    const { isFeatureFlagWhiteListed } = useFeatureFlag();
    const { t } = useTranslation();
    const appDomain = useSelector(selectAppDomain);
    const [isUnlinkModalOpen, setIsUnlinkModalOpen] = useState(false);
    const [selectedBankAccount, setSelectedBankAccount] = useState(null);
    const [invalidateConsent] = useInvalidateConsentMutation();
    const isPluggyTransactionsFeatureEnabled = isFeatureFlagWhiteListed(appDomain === AppDomain.EMPRESA
        ? FeatureFlagUseCase.PLUGGY_TRANSACTIONS_LIST_CLIENT
        : FeatureFlagUseCase.PLUGGY_TRANSACTIONS_LIST_ACCOUNTANT);
    const isPluggyOthersBanksBalanceFeatureEnabled = isFeatureFlagWhiteListed(appDomain === AppDomain.EMPRESA
        ? FeatureFlagUseCase.PLUGGY_OTHER_BANKS_BALANCE_CLIENT
        : FeatureFlagUseCase.PLUGGY_OTHER_BANKS_BALANCE_ACCOUNTANT);
    const onCloseUnlinkModal = () => {
        setIsUnlinkModalOpen(false);
        setSelectedBankAccount(null);
    };
    const handleOnCancelConnection = async () => {
        if (selectedBankAccount) {
            onInvalidateConsentStart();
            try {
                await invalidateConsent({
                    consentId: selectedBankAccount.id,
                    partner: selectedBankAccount.partner,
                }).unwrap();
                await onCancelConnection?.();
            }
            finally {
                onInvalidateConsentEnd();
            }
        }
        else {
            await onCancelConnection?.();
        }
        onCloseUnlinkModal();
    };
    const showBalance = useAppSelector(state => state.showBalance.showBalance);
    if (!consentBanks)
        return null;
    return (_jsxs("div", { className: styles.bankAccountList, "data-testid": dataTestId, children: [_jsx("h4", { className: `${styles.cardTitle} ${styles.semi}`, "data-testid": 'textViewBankAccountListTitle', children: appDomain === AppDomain.EMPRESA
                    ? t('contabilidade-digital.bank-account-list.title-empresa')
                    : t('contabilidade-digital.bank-account-list.title-escritorio') }), _jsx("ul", { className: styles.accountsList, "data-testid": 'listBankAccountListAccounts', children: consentBanks.map(bankAccount => {
                    const externalInstitutionName = bankAccount.externalInstitutionName.toUpperCase();
                    const bankInfo = bankData[externalInstitutionName];
                    let label;
                    let logo;
                    if (bankInfo) {
                        label = bankInfo.label;
                        logo = bankInfo.logo;
                    }
                    else {
                        label = bankAccount.externalInstitutionDescription;
                        logo = '';
                    }
                    let consentBankState = bankAccount.state.toUpperCase();
                    if (bankAccount.partner !== ConsentPartner.PLUGGY && bankAccount.state !== ConsentBankState.CONNECTED) {
                        consentBankState = ConsentBankState.REDO_CONSENTMENT;
                    }
                    const canUnlinkAccount = bankAccount.state !== ConsentBankState.CONNECTING;
                    let balance = '*****';
                    if (showBalance) {
                        balance = [
                            bankAccount.partner === ConsentPartner.PLUGGY,
                            bankAccount.balance !== undefined,
                            bankAccount.state === ConsentBankState.CONNECTED,
                        ].every(Boolean)
                            ? formatCurrency(bankAccount.balance ?? 0)
                            : '';
                    }
                    return (_jsxs("li", { "data-testid": `listItemBankAccountListBank${bankAccount.id}`, children: [_jsx(BankLogoAvatar, { externalBankImageUrl: bankAccount.externalBankImageUrl, fallbackLogo: logo, className: styles.avatar, testId: bankAccount.id }), _jsx("span", { "data-testid": `textViewBankAccountListLabel${bankAccount.id}`, children: label ?? bankAccount.externalInstitutionName }), isPluggyOthersBanksBalanceFeatureEnabled && (_jsx("span", { className: styles.labelBalance, "data-testid": `textViewBankAccountListBalance${bankAccount.id}`, children: balance })), _jsxs("span", { className: `${styles.statusWrapper} ${statesStyles[consentBankState]}`, "data-testid": `textViewBankAccountListState${bankAccount.id}`, children: [t(statesTranslationKeys[consentBankState]), stateHasTooltip.includes(consentBankState) && (_jsx(Tooltip, { content: t(statesTranslationKeys[consentBankState] + '-tooltip'), position: 'right', children: _jsx(Icon, { name: 'information', "data-testid": `iconBankAccountListInfo${bankAccount.id}` }) }))] }), _jsx("span", { children: _jsx(Button, { variant: 'tertiary', disabled: !canUnlinkAccount, size: 'large', leftIcon: 'linkBreak', dataTestId: `buttonBankAccountListLinkBreak${bankAccount.id}`, onClick: () => {
                                        if (!canUnlinkAccount)
                                            return;
                                        setSelectedBankAccount(bankAccount);
                                        setIsUnlinkModalOpen(true);
                                    } }) }), _jsx("span", { className: styles.chevronIconWrapper, children: consentBankState === ConsentBankState.CONNECTED && isPluggyTransactionsFeatureEnabled && (_jsx(Icon, { name: 'arrowChevronRight', className: styles.chevronIcon, "data-testid": `iconBankAccountListChevron${bankAccount.id}`, onClick: () => onOpenTransactions({
                                        accountId: getAccountToOpen(bankAccount.connectedAccounts),
                                        accountName: label,
                                        accountNumber: undefined,
                                        bankLogo: logo,
                                        balance: bankAccount?.balance ?? 0,
                                    }) })) })] }, bankAccount.id));
                }) }), _jsx(UnlinkAccountModal, { isOpen: isUnlinkModalOpen, onClose: onCloseUnlinkModal, onCancelConnection: handleOnCancelConnection })] }));
};
export default BankAccountList;
